import { Suspense, useLayoutEffect, useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Switch, Redirect } from 'react-router-dom';

import { DBET_USER_DATA } from 'constants/auth';
import { METAMASK_NETWORK_CHANGED } from 'constants/dialogs';
import { store } from 'store';
import {
  setDbetData,
  dbetIsLoaded,
  restoreLogin,
  setProviderData,
  setUserSetting,
  logout,
  closeDialog,
  setHideFrozenAmount,
  openDialog,
} from 'store/actions';
import { useMount } from 'utils/hooks';
import { TranslatorProvider } from 'utils/translator';
import { getCookie, DbetSingleton, loadState, PolyfillManager, initFonts } from 'utils';
import Layout from 'layout';
import Loader from 'components/loader';
import ErrorBoundary from 'components/errorBoundary';
import Popups from 'sections/popups';
import Dialogs from 'sections/dialogs';
import config from 'configs';

const HOORY_WEBSITE_TOKEN =
  process.env?.REACT_APP_HOORY_WEBSITE_TOKEN || 'da9Jet28eYWWHsnx7reqNTDG';
const HOORY_BASE_URL = process.env?.REACT_APP_HOORY_BASE_URL || 'https://app.hoory.com';

const { content: { withHoory = false } = {} } = config;

const App = () => {
  useMount(async () => {
    new PolyfillManager();

    // initialize font by config read more about that in /partnerControl/readme.mdx
    initFonts();

    const userCredentials =
      getCookie(DBET_USER_DATA) || loadState(DBET_USER_DATA, 'sessionStorage');

    async function init() {
      try {
        const dbet = await DbetSingleton.init();
        store.dispatch(setDbetData(dbet));

        const response = await dbet.getCurrencyAndProvider();
        store.dispatch(setProviderData(response));

        dbet.registerCallbacks({
          1: () => {
            store.dispatch(openDialog(METAMASK_NETWORK_CHANGED));
            store.dispatch(setHideFrozenAmount(true));
            store.dispatch(setUserSetting({ loginFinished: false }));
          },
          2: () => {
            store.dispatch(setHideFrozenAmount(false));
            store.dispatch(closeDialog(METAMASK_NETWORK_CHANGED));
            setTimeout(() => {
              store.dispatch(setUserSetting({ loginFinished: true }));
            }, 1000);
          },
          3: async () => {
            await store.dispatch(logout());
          },
        });

        if (userCredentials) {
          store.dispatch(restoreLogin(userCredentials));
        } else {
          store.dispatch(setUserSetting({ loginFinished: true }));
        }

        store.dispatch(dbetIsLoaded());
      } catch (error) {
        console.error(error);
      }
    }

    await init();

    if (process.env.NODE_ENV === 'development') {
      import('eruda').then(eruda => eruda.default.init());
    }
  });

  useLayoutEffect(() => {
    if (document) {
      document.title = config?.content?.title || 'DECAplay';
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && withHoory) {
      const hooryScript = document.createElement('script');

      hooryScript.innerText = `window.hoorySettings = { position: 'right', type: 'standard', launcherTitle: 'Chat with us' };
      (function (d, t) {
        var BASE_URL = '${HOORY_BASE_URL}';
        var g = d.createElement(t),
          s = d.getElementsByTagName(t)[0];
        g.src = BASE_URL + '/packs/js/sdk.js';
        g.defer = true;
        g.async = true;
        s.parentNode.insertBefore(g, s);
        g.onload = function () {
          window.hoorySDK.run({
            websiteToken: '${HOORY_WEBSITE_TOKEN}',
            baseUrl: BASE_URL,
          });
        };
      })(document, 'script');`;

      document.body.insertAdjacentElement('afterend', hooryScript);
    }
  }, []);

  return (
    <Provider store={store}>
      <TranslatorProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <Suspense fallback={<Loader />}>
              <Switch>
                <Redirect exact from="/" to="/casino" />
                <Layout />
              </Switch>
            </Suspense>
            <Popups />
            <Dialogs />
          </BrowserRouter>
        </ErrorBoundary>
      </TranslatorProvider>
    </Provider>
  );
};

export default App;
